import * as turf from "@turf/turf";

// Maximum total area (m²) allowed for a single area assessment. Defaults to 500,000 m².
export const MAX_TOTAL_AREA_SQM = 500000;

// Default mapbox-gl-draw modes we drive from the custom toolbar.
// STATIC is a custom, non-interactive mode (registered in <DrawControl>) used to
// lock the drawn polygons while an assessment result is shown.
export const DRAW_MODES = {
  SELECT: "simple_select",
  DRAW: "draw_polygon",
  EDIT: "direct_select",
  STATIC: "static",
};

// Deep clone a draw FeatureCollection so undo/redo snapshots are immutable.
export const cloneFeatureCollection = (featureCollection) =>
  JSON.parse(
    JSON.stringify(
      featureCollection || { type: "FeatureCollection", features: [] },
    ),
  );

export const isPolygon = (feature) =>
  feature?.geometry?.type === "Polygon" ||
  feature?.geometry?.type === "MultiPolygon";

// Stable identity of a polygon's shape — used to key measure requests and to
// detect which polygons changed since the last successful measurement.
export const getGeometryKey = (geometry) =>
  JSON.stringify(geometry?.coordinates ?? null);

// Area in square meters.
export const getPolygonArea = (feature) => {
  try {
    return turf.area(feature);
  } catch {
    return 0;
  }
};

// Perimeter in meters (length of the polygon outline as a line).
export const getPolygonPerimeter = (feature) => {
  try {
    const line = turf.polygonToLine(feature);
    // polygonToLine can return a Feature or FeatureCollection (with holes).
    const lengthKm =
      line.type === "FeatureCollection"
        ? line.features.reduce(
            (sum, f) => sum + turf.length(f, { units: "kilometers" }),
            0,
          )
        : turf.length(line, { units: "kilometers" });
    return lengthKm * 1000;
  } catch {
    return 0;
  }
};

// [lng, lat] centroid for placing the on-map metric label.
export const getPolygonCentroid = (feature) => {
  try {
    return turf.centroid(feature).geometry.coordinates;
  } catch {
    return null;
  }
};

export const formatArea = (squareMeters, t) => {
  if (!squareMeters || squareMeters <= 0)
    return `0 ${t("risksMap.polygon.units.squareMeters")}`;
  if (squareMeters >= 1e6) {
    return `${(squareMeters / 1e6).toFixed(2)} ${t("risksMap.polygon.units.squareKilometers")}`;
  }
  return `${Math.round(squareMeters).toLocaleString()} ${t("risksMap.polygon.units.squareMeters")}`;
};

export const formatPerimeter = (meters, t) => {
  if (!meters || meters <= 0) return `0 ${t("risksMap.polygon.units.meters")}`;
  if (meters >= 1000) {
    return `${(meters / 1000).toFixed(2)} ${t("risksMap.polygon.units.kilometers")}`;
  }
  return `${Math.round(meters).toLocaleString()} ${t("risksMap.polygon.units.meters")}`;
};

// Build per-polygon metrics from a draw FeatureCollection.
export const computePolygonMetrics = (featureCollection) => {
  const features = featureCollection?.features || [];
  return features.filter(isPolygon).map((feature) => ({
    id: feature.id,
    area: getPolygonArea(feature),
    perimeter: getPolygonPerimeter(feature),
    centroid: getPolygonCentroid(feature),
  }));
};

// Outer ring of the polygon currently being drawn. mapbox-gl-draw keeps the
// in-progress feature selected, so getSelected() targets *that* polygon even
// when other finished polygons exist on the map.
export const getActiveDrawingRing = (draw) => {
  try {
    const selected = draw.getSelected();
    console.log("selected", selected);
    const feature = (selected?.features || []).find(
      (f) =>
        f.geometry?.type === "Polygon" &&
        Array.isArray(f.geometry.coordinates?.[0]),
    );
    console.log("feature", feature);
    return feature ? feature.geometry.coordinates[0] : null;
  } catch {
    return null;
  }
};

// Vertices the user has actually committed for the in-progress polygon.
// The outer ring is [...placed, liveCursorPoint, autoClosingPoint], so the
// committed count is the ring length minus those two trailing points.
export const getCommittedVertexCount = (ring) =>
  Array.isArray(ring) ? Math.max(0, ring.length - 2) : 0;
